import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, BACKEND_URL } from "@/lib/api";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Film, ChevronLeft, Star, CheckCircle2, BookmarkPlus, Loader2, Globe, Tag as TagIcon, CopyPlus, ArrowDownToLine } from "lucide-react";
import { toast } from "sonner";
import PublicMovieDetailDialog from "@/components/PublicMovieDetailDialog";

const FALLBACK = "https://images.unsplash.com/photo-1489599849927-2ee91cede3ba?w=600";

export default function PublicProfile() {
  const { username } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [profile, setProfile] = useState(null);
  const [groups, setGroups] = useState([]);
  const [movies, setMovies] = useState([]);
  const [tags, setTags] = useState([]);
  const [activeGroup, setActiveGroup] = useState(null);
  const [activeTag, setActiveTag] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copyingAll, setCopyingAll] = useState(false);
  const [copyingGroup, setCopyingGroup] = useState(null);
  const [openMovie, setOpenMovie] = useState(null);
  const [copyingMovie, setCopyingMovie] = useState(null);

  // Public endpoints don't require auth — use bare axios so cookies don't accidentally turn it into an auth attempt.
  const pub = axios.create({ baseURL: `${BACKEND_URL}/api` });

  const load = async () => {
    setLoading(true);
    try {
      const [p, g, t, m] = await Promise.all([
        pub.get(`/u/${username}`),
        pub.get(`/u/${username}/groups`),
        pub.get(`/u/${username}/tags`),
        pub.get(`/u/${username}/movies`),
      ]);
      setProfile(p.data);
      setGroups(g.data);
      setTags(t.data);
      setMovies(m.data);
    } catch (e) {
      setError(e.response?.data?.detail || "Profile not available");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [username]);

  const filterMovies = async (group_id, tag) => {
    setActiveGroup(group_id || null);
    setActiveTag(tag || null);
    const params = {};
    if (group_id) params.group_id = group_id;
    if (tag) params.tag = tag;
    const r = await pub.get(`/u/${username}/movies`, { params });
    setMovies(r.data);
  };

  const copyMovie = async (mid, mark_as = "backlog") => {
    if (!user) { toast.error("Sign in to copy"); return; }
    setCopyingMovie(mid);
    try {
      const r = await api.post(`/u/${username}/movies/${mid}/copy`, null, { params: { mark_as } });
      toast.success(r.data.duplicate ? "Already in your library" : `Copied "${r.data.movie.title}"`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to copy");
    } finally { setCopyingMovie(null); }
  };

  const copyGroup = async (gid) => {
    if (!user) { toast.error("Sign in to copy"); return; }
    setCopyingGroup(gid);
    try {
      const r = await api.post(`/u/${username}/groups/${gid}/copy`);
      toast.success(`Copied ${r.data.created} new movie${r.data.created === 1 ? "" : "s"} into "${r.data.group.name}" (${r.data.duplicates} duplicates)`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to copy group");
    } finally { setCopyingGroup(null); }
  };

  const copyAll = async () => {
    if (!user) { toast.error("Sign in to copy"); return; }
    if (!window.confirm(`Copy ALL of @${username}'s public movies (${profile?.stats?.movies}) into your backlog?`)) return;
    setCopyingAll(true);
    try {
      const r = await api.post(`/u/${username}/copy-all`);
      toast.success(`Copied ${r.data.created} new movies (${r.data.duplicates} duplicates)`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setCopyingAll(false); }
  };

  if (loading) return <div className="min-h-[60vh] flex items-center justify-center text-slate-500 text-sm tracking-[0.2em] uppercase">Loading…</div>;
  if (error) return (
    <div className="max-w-xl mx-auto py-20 text-center">
      <Lock className="w-10 h-10 mx-auto text-slate-600 mb-4" />
      <h2 className="font-display text-3xl font-bold mb-2">Not available</h2>
      <p className="text-slate-400 text-sm mb-6">{error}</p>
      <Button onClick={() => nav(user ? "/dashboard" : "/")} className="bg-[#E11D48] hover:bg-rose-700">Go back</Button>
    </div>
  );

  const isMine = user && user.username === username;

  return (
    <div className="space-y-8" data-testid={`public-profile-${username}`}>
      <div className="flex items-center gap-2 text-xs text-slate-500">
        {user && (
          <Link to="/dashboard" className="flex items-center gap-1 hover:text-white"><ChevronLeft className="w-3 h-3" /> Back to your library</Link>
        )}
      </div>

      <div className="flex flex-col md:flex-row gap-6 items-start">
        <div className="w-20 h-20 rounded-full overflow-hidden border border-white/10 shrink-0 bg-[#1A1A1E]">
          {profile.picture ? (
            <img src={profile.picture} alt="" className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-2xl text-slate-500">{profile.name?.[0]?.toUpperCase()}</div>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <Globe className="w-4 h-4 text-[#10B981]" />
            <span className="text-xs tracking-[0.3em] uppercase text-[#10B981] font-bold">Public profile</span>
          </div>
          <h1 className="font-display text-4xl font-bold tracking-tighter mt-1">{profile.name}</h1>
          <div className="text-slate-500 text-sm mt-1">@{profile.username}</div>
          {profile.bio && <p className="text-slate-300 text-sm mt-3 max-w-xl">{profile.bio}</p>}
          <div className="flex items-center gap-5 mt-4 text-sm text-slate-400">
            <span><span className="text-white font-semibold">{profile.stats.movies}</span> movies</span>
            <span><span className="text-[#10B981] font-semibold">{profile.stats.watched}</span> watched</span>
            <span><span className="text-white font-semibold">{profile.stats.groups}</span> groups</span>
          </div>
        </div>
        {!isMine && user && profile.stats.movies > 0 && (
          <Button onClick={copyAll} disabled={copyingAll} data-testid="copy-all-btn"
                  className="bg-[#E11D48] hover:bg-rose-700 rounded-full">
            {copyingAll ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <ArrowDownToLine className="w-4 h-4 mr-1.5" />}
            Copy entire library
          </Button>
        )}
      </div>

      {groups.length > 0 && (
        <div className="space-y-3">
          <div className="text-xs uppercase tracking-[0.2em] text-[#E11D48] font-bold">Groups</div>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => filterMovies(null, null)}
              data-testid="filter-all"
              className={`px-3 py-1.5 rounded-full text-xs border transition-all ${
                !activeGroup && !activeTag ? "bg-[#E11D48] border-[#E11D48] text-white" : "bg-transparent border-white/10 text-slate-300 hover:border-white/30"
              }`}>All</button>
            {groups.map(g => (
              <div key={g.id} className="flex items-center gap-1">
                <button
                  onClick={() => filterMovies(g.id, null)}
                  data-testid={`filter-group-${g.id}`}
                  className={`px-3 py-1.5 rounded-full text-xs border transition-all ${
                    activeGroup === g.id ? "border-2" : "border bg-transparent text-slate-300 hover:border-white/30"
                  }`}
                  style={activeGroup === g.id ? { background: g.color + "22", borderColor: g.color, color: g.color } : { borderColor: "rgba(255,255,255,0.1)" }}>
                  {g.name} <span className="opacity-60">· {g.movie_count || 0}</span>
                </button>
                {!isMine && user && (
                  <button
                    onClick={() => copyGroup(g.id)}
                    disabled={copyingGroup === g.id}
                    data-testid={`copy-group-${g.id}`}
                    title={`Copy "${g.name}" into your library`}
                    className="p-1.5 rounded-full hover:bg-white/10 text-slate-400 hover:text-white">
                    {copyingGroup === g.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CopyPlus className="w-3.5 h-3.5" />}
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {tags.length > 0 && (
        <div className="space-y-3">
          <div className="text-xs uppercase tracking-[0.2em] text-[#E11D48] font-bold">Tags</div>
          <div className="flex flex-wrap gap-2">
            {tags.map(t => (
              <button
                key={t}
                onClick={() => filterMovies(null, t)}
                data-testid={`filter-tag-${t}`}
                className={`px-3 py-1 rounded-full text-xs border transition-all flex items-center gap-1 ${
                  activeTag === t ? "bg-[#E11D48] border-[#E11D48] text-white" : "bg-transparent border-white/10 text-slate-300 hover:border-white/30"
                }`}>
                <TagIcon className="w-3 h-3" /> {t}
              </button>
            ))}
          </div>
        </div>
      )}

      <div>
        <div className="text-xs uppercase tracking-[0.2em] text-[#E11D48] font-bold mb-3">
          {activeGroup ? `Movies in ${groups.find(g => g.id === activeGroup)?.name || ""}` : activeTag ? `Tagged "${activeTag}"` : "All public movies"}
          <span className="text-slate-500 normal-case ml-2">· {movies.length}</span>
        </div>
        {movies.length === 0 ? (
          <div className="text-center py-20 border border-dashed border-white/10 rounded-2xl">
            <Film className="w-10 h-10 mx-auto text-slate-600 mb-4" />
            <p className="text-slate-400">Nothing to show yet.</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4" data-testid="public-movies-grid">
            {movies.map(m => {
              const seasons = m.seasons || [];
              const isTV = m.media_type === "tv" && seasons.length > 0;
              const watchedSeasons = seasons.filter(s => s.status === "watched").length;
              return (
                <div key={m.id} className="poster-card relative group" data-testid={`public-movie-${m.id}`}>
                  <div
                    className="relative aspect-[2/3] rounded-lg overflow-hidden bg-[#1A1A1E] cursor-pointer"
                    onClick={() => setOpenMovie(m)}
                    data-testid={`public-open-detail-${m.id}`}
                  >
                    <img src={m.poster || FALLBACK} alt={m.title}
                         className="w-full h-full object-cover"
                         onError={e => { e.target.src = FALLBACK; }} />
                    {m.status === "watched" && (
                      <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                        <CheckCircle2 className="w-10 h-10 text-[#10B981]" />
                      </div>
                    )}
                    {isTV && (
                      <div className="absolute top-2 left-2 bg-black/70 backdrop-blur rounded-full px-2 py-0.5 text-[10px] font-medium tracking-wider">
                        {watchedSeasons === seasons.length
                          ? <span className="text-[#10B981]">ALL · {seasons.length}S</span>
                          : <span><span className="text-[#10B981]">{watchedSeasons}</span><span className="text-slate-400">/{seasons.length} S</span></span>}
                      </div>
                    )}
                    {m.imdb_rating && m.imdb_rating !== "N/A" && parseFloat(m.imdb_rating) > 0 && (
                      <div className="absolute top-2 right-2 bg-black/70 backdrop-blur rounded-full px-2 py-0.5 text-xs flex items-center gap-1">
                        <Star className="w-3 h-3 text-[#F59E0B] fill-[#F59E0B]" /> {Number(parseFloat(m.imdb_rating).toFixed(1))}
                      </div>
                    )}
                    {!isMine && user && (
                      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex flex-col justify-end p-3">
                        <Button
                          onClick={(e) => { e.stopPropagation(); copyMovie(m.id); }}
                          size="sm" data-testid={`copy-movie-${m.id}`}
                          className="w-full bg-[#E11D48] hover:bg-rose-700 rounded-full text-xs h-8">
                          <BookmarkPlus className="w-3.5 h-3.5 mr-1" /> Copy to my library
                        </Button>
                      </div>
                    )}
                  </div>
                  <div className="mt-2.5">
                    <div className="text-sm font-medium line-clamp-1" title={m.title}>{m.title}</div>
                    <div className="text-xs text-slate-500 mt-0.5">{m.year}</div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <PublicMovieDetailDialog
        movie={openMovie}
        open={!!openMovie}
        onOpenChange={(v) => !v && setOpenMovie(null)}
        canCopy={!!user && !isMine}
        copying={copyingMovie === openMovie?.id}
        onCopy={(mid) => copyMovie(mid)}
      />
    </div>
  );
}

function Lock(props) {
  // tiny inline icon to avoid second import block
  return <svg {...props} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" /></svg>;
}
